import React, { useState } from 'react';
import useInfoForCurrentlySelectedMarket from '../hooks/useInfoForCurrentlySelectedMarket';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import FundingRateInfo from './PerpPageStats/FundingRateInfo';
import GridSectionWrapper from './GridSections/GridSectionWrapper/GridSectionWrapper';
import Chevron from './Icons/Chevron';
import MarketSelectorDrawer from './MarketSelectorDrawer';
import Text from './Text/Text';
import MarketIcon from './Utils/MarketIcon';
import MarketLeverageIndicator from './Utils/MarketLeverageIndicator';
import NoInsuranceWarningTooltip from './Utils/NoInsuranceWarningTooltip';
import { OracleSource, PerpMarketAccount } from '@drift-labs/sdk';
import { ENUM_UTILS } from '@drift/common';
import Tooltip from './Tooltip/Tooltip';

/**
 * Displays the clickable market selector, which opens a dropdown drawer of markets to select from.
 * This element recursively renders itself (with the inDrawer state) and a drawer below it when expanded
 * @param props
 * @returns
 */
export const MarketSelectorDisplay = (props: {
	inDrawer?: boolean;
	onClose?: () => void;
	mobile?: boolean;
}) => {
	const boxId = 'marketSelector';

	const currentlySelectedMarketId = useDriftStore(
		(s) => s.selectedMarket.marketId
	);
	const currentlySelectedMarketSymbol = useDriftStore(
		(s) => s.selectedMarket.current.market.symbol
	);
	const currentlySelectedMarketName = useDriftStore(
		(s) => s.selectedMarket.current.marketName
	);
	const currentlySelectedMarketOracleSource = useDriftStore(
		(s) => s.selectedMarket.current.market.oracleSource
	);

	const isPerp = currentlySelectedMarketId.isPerp;
	const currentlySelectedMarketInfo = useInfoForCurrentlySelectedMarket();

	const showInsuranceFundWarning =
		currentlySelectedMarketInfo.showInsuranceFundWarning;

	const [showMarketSelectorModal, setShowMarketSelectorModal] = useState(false);

	const handleClose = () => {
		setShowMarketSelectorModal(false);
	};

	const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
		e.preventDefault();
		e.stopPropagation();

		if (showMarketSelectorModal) {
			setShowMarketSelectorModal(false);
			return;
		} else {
			setShowMarketSelectorModal(true);
		}
	};

	const rootSelector = document.getElementById(boxId);

	return (
		<GridSectionWrapper
			style={
				props.inDrawer
					? {
							height: `${rootSelector.getBoundingClientRect().height}px`,
							width: `${rootSelector.getBoundingClientRect().width}px`,
					  }
					: {}
			}
			noPad
			className={`border-0 rounded-sm ${
				props.mobile ? 'w-full' : 'w-[280px] min-w-[250px]'
			}`}
		>
			<div
				id={props.inDrawer ? '' : boxId}
				className={`z-40 change-market-inner h-full flex flex-fill flex-row w-full items-center justify-between bg-container-bg text-text-default border-container-border relative group hover:cursor-pointer sm:p-2 border border-r-0`}
				onMouseDown={handleClick}
			>
				<div className="flex flex-row items-center justify-between w-full asset-dropdown-header flex-fill xs:px-4 sm:px-2 sm:pr-0">
					<div className="flex items-center">
						<MarketIcon
							marketSymbol={currentlySelectedMarketSymbol}
							className="mr-2"
							isSpot={!isPerp}
							customHeight={18}
							customWidth={18}
						/>
						{ENUM_UTILS.match(
							currentlySelectedMarketOracleSource,
							OracleSource.PYTH_1K
						) ||
						ENUM_UTILS.match(
							currentlySelectedMarketOracleSource,
							OracleSource.PYTH_1M
						) ? (
							<Tooltip
								placement="top"
								content={
									<span>{`This perp represents the price of ${currentlySelectedMarketSymbol} * ${
										ENUM_UTILS.match(
											currentlySelectedMarketOracleSource,
											OracleSource.PYTH_1M
										)
											? '1 million'
											: '1 thousand'
									}`}</span>
								}
							>
								<Text.H4 className={`text-text-default`}>
									{currentlySelectedMarketName}
								</Text.H4>
							</Tooltip>
						) : (
							<Text.H4 className={`text-text-default`}>
								{currentlySelectedMarketName}
							</Text.H4>
						)}

						<span className="pl-1.5">
							<MarketLeverageIndicator
								marketIndex={currentlySelectedMarketId.marketIndex}
								marketType={currentlySelectedMarketId.marketType}
							/>
						</span>
						{showInsuranceFundWarning && (
							<div className="ml-2">
								<NoInsuranceWarningTooltip
									marketInfo={
										currentlySelectedMarketInfo.info
											.account as PerpMarketAccount
									}
								/>
							</div>
						)}
					</div>
					{props.inDrawer ? (
						<button className="flex items-center space-x-2 text-xs text-neutrals-50 group-hover:text-yellow-50">
							<Chevron direction="up" className="w-4 h-4 p-1" />
						</button>
					) : (
						<button className="flex items-center space-x-2 text-xs text-text-label">
							<Chevron
								direction={showMarketSelectorModal ? 'up' : 'down'}
								className={`h-4 w-4 mx-1`}
							/>
						</button>
					)}
				</div>
			</div>
			{showMarketSelectorModal && (
				<MarketSelectorDrawer
					onClose={handleClose}
					className={``}
					targetId={boxId}
				/>
			)}
		</GridSectionWrapper>
	);
};

const MarketSelector = () => {
	return (
		<div className="inline-flex items-center justify-center w-full h-full thin-scroll">
			<MarketSelectorDisplay />
			<FundingRateInfo />
		</div>
	);
};

export default MarketSelector;
